import React from 'react'
import { createHashRouter, RouteObject, Navigate } from 'react-router-dom'
import ErrorPage from './components/error-page'
import { getDefaultLayout } from './components/layout'
import { getAdminLayout } from './components/admin/dashboard-layout'
import { LoginPage } from './pages/auth/login'
import { ForgotPasswordPage } from './pages/auth/forgot-password'
import { ResetPasswordPage } from './pages/auth/reset-password'
import { CompleteInvitePage } from './pages/auth/complete-invite'
import { ProtectedRoute } from './components/protected-route'

// Define public routes that don't require authentication
const publicPaths = [
  '/',
  '/auth/reset-password',
  '/auth/forgot-password',
  '/auth/login',
  '/auth/complete-invite'
];

export const routerObjects: RouteObject[] = [
  {
    path: '/auth/complete-invite',
    element: <CompleteInvitePage />,
    children: [
      {
        path: '*',
        element: <CompleteInvitePage />
      }
    ]
  },
  {
    path: '/auth/reset-password',
    element: <ResetPasswordPage />,
    children: [
      {
        path: '*',
        element: <ResetPasswordPage />
      }
    ]
  },
  {
    path: '/',
    element: <Navigate to="/admin" replace />,
  },
  {
    path: '/admin',
    Component: React.lazy(() => import('./pages/admin')),
    children: [
      {
        path: '',
        Component: React.lazy(() => import('./pages/admin/dashboard')),
      },
      {
        path: 'users',
        Component: React.lazy(() => import('./pages/admin/users')),
      },
      {
        path: 'profile',
        Component: React.lazy(() => import('./pages/admin/profile')),
      },
      {
        path: 'audit',
        Component: React.lazy(() => import('./pages/admin/audit')),
      },
      {
        path: 'partners',
        Component: React.lazy(() => import('./pages/admin/partners')),
      },
      {
        path: 'integrations',
        Component: React.lazy(() => import('./pages/admin/integrations')),
      },
    ],
  },
  {
    path: '/auth/login',
    Component: LoginPage,
  },
  {
    path: '/auth/forgot-password',
    Component: ForgotPasswordPage,
  },
  {
    path: '*',
    element: <Navigate to="/" replace />,
  },
]

export function createRouter(): ReturnType<typeof createHashRouter> {
  console.log('[Router] Creating router with public paths:', publicPaths)

  // Create base routes without any wrappers
  const baseRoutes = routerObjects.map(route => {
    // Don't wrap public routes with auth protection
    const isPublicRoute = publicPaths.some(path => route.path?.startsWith(path))
    const requiresAuth = !isPublicRoute && (route.path === '/' || route.path?.startsWith('/admin/'))
    const requiresAdmin = route.path?.startsWith('/admin/')

    console.log('[Router] Processing route:', {
      path: route.path,
      isPublicRoute,
      requiresAuth,
      requiresAdmin,
      hasComponent: !!route.Component,
      hasElement: !!route.element
    })

    let element = route.element
    const Component = route.Component

    if (Component) {
      const LazyComponent = Component
      type LayoutComponent = { getLayout?: typeof getDefaultLayout }
      const getLayout = (LazyComponent as LayoutComponent).getLayout || getDefaultLayout

      console.log('[Router] Setting up component:', {
        path: route.path,
        hasCustomLayout: !!(LazyComponent as LayoutComponent).getLayout,
        willUseDefaultLayout: !(LazyComponent as LayoutComponent).getLayout
      })

      let content = (
        <React.Suspense fallback={<div>Loading...</div>}>
          <LazyComponent />
        </React.Suspense>
      )

      // For admin routes, wrap with admin layout
      if (route.path?.startsWith('/admin')) {
        console.log('[Router] Applying admin layout for path:', route.path)
        content = getAdminLayout(content)
      } else if (!isPublicRoute) {
        console.log('[Router] Applying default layout for path:', route.path)
        content = getLayout(content)
      } else {
        console.log('[Router] No layout applied for public path:', route.path)
      }

      if (requiresAuth) {
        console.log('[Router] Adding auth protection for path:', route.path, { requiresAdmin })
        content = <ProtectedRoute requireAdmin={requiresAdmin}>{content}</ProtectedRoute>
      }

      element = content
    }

    return {
      ...route,
      element: element || route.element,
      Component: null,
      ErrorBoundary: ErrorPage,
    }
  })

  console.log('[Router] Final routes configuration:', baseRoutes.map(r => ({
    path: r.path,
    hasErrorBoundary: !!r.ErrorBoundary,
    hasChildren: !!r.children?.length,
    isProtected: React.isValidElement(r.element) && r.element.type === ProtectedRoute
  })))

  const router = createHashRouter(baseRoutes)
  console.log('[Router] Router created successfully')
  return router
}

export const router: ReturnType<typeof createHashRouter> = createRouter()
